import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { addDays, format } from 'date-fns'
import { Ban, CalendarDays, Camera, Check, Loader2, Lock, RefreshCw, Search, TriangleAlert } from 'lucide-react'
import { customersApi } from '@/api/customers'
import { customerCategoriesApi } from '@/api/customer-categories'
import { useCreateServiceRecord, useServiceTypeOptions, useUpdateServiceRecord } from '@/hooks/useServiceRecords'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { MultiImageUploadField } from '@/components/ui/multi-image-upload'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { showError } from '@/lib/error'
import { serviceRecordFormSchema, type ServiceRecordFormValues } from '@/schemas/service-record'
import type { Customer } from '@/types/customer'
import type { ServiceRecord, ServiceType } from '@/types/service-record'
import { cn } from '@/lib/utils'

interface ServiceRecordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** 预填客户（客户列表行入口传入；编辑模式忽略） */
  customer?: Customer
  /** 编辑的记录（传入即编辑模式，客户不可改） */
  record?: ServiceRecord
  onSaved?: () => void
}

/** 快捷回访日期：老 OA 提醒窗口为 3 天，故 3 天后是高频选择 */
const QUICK_DATE_OPTIONS = [
  { label: '明天', days: 1 },
  { label: '3天后', days: 3 },
  { label: '一周后', days: 7 },
]


function formatDateTime(value?: string | null): string {
  if (!value) return ''
  return value.replace('T', ' ').slice(0, 16)
}

/** 服务类型芯片：一点即选，选中即着色；stopped 为记录上保留的已停用类型，可移除不可再选 */
function TypeChip({ type, selected, onToggle, stopped = false }: { type: ServiceType; selected: boolean; onToggle: () => void; stopped?: boolean }) {
  const chip = (
    <button
      type="button"
      aria-pressed={selected}
      title={stopped ? '该类型已停用：可在此记录中保留或移除，移除后不可再选' : undefined}
      onClick={onToggle}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors',
        stopped
          ? 'border-dashed bg-muted/60 text-muted-foreground hover:bg-muted'
          : selected
            ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
            : 'border-input bg-background hover:bg-accent hover:text-accent-foreground',
      )}
    >
      {selected && !stopped && <Check className="size-3.5 shrink-0" />}
      {stopped && <Ban className="size-3.5 shrink-0" />}
      {type.is_avatar && (
        <Camera className={cn('size-3.5 shrink-0', !selected && !stopped && 'text-muted-foreground')} />
      )}
      {type.label}
      {stopped && <span className="text-xs">已停用</span>}
    </button>
  )

  if (!type.is_avatar) return chip
  return (
    <Tooltip>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent>该类型要求上传图片</TooltipContent>
    </Tooltip>
  )
}

export function ServiceRecordDialog({ open, onOpenChange, customer, record, onSaved }: ServiceRecordDialogProps) {
  const isEdit = !!record
  const [keyword, setKeyword] = useState('')
  const [highlightIndex, setHighlightIndex] = useState(0)
  const [pickedCustomer, setPickedCustomer] = useState<Customer | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const subjectInputRef = useRef<HTMLInputElement | null>(null)

  const form = useForm<ServiceRecordFormValues>({
    resolver: zodResolver(serviceRecordFormSchema),
    defaultValues: {
      customer_id: 0,
      type_ids: [],
      subject: '',
      content: '',
      images: [],
      next_service_at: '',
      category_id: '',
    },
  })

  const { data: typeOptions, isLoading: typesLoading } = useServiceTypeOptions()
  const { data: categories } = useQuery({
    queryKey: ['customer-categories', 'options'],
    queryFn: () => customerCategoriesApi.options(),
    enabled: open && !isEdit,
  })

  const customerId = form.watch('customer_id')
  const typeIds = form.watch('type_ids')
  const images = form.watch('images')

  // 客户搜索（仅录入模式且未选定客户时）
  const trimmedKeyword = keyword.trim()
  const { data: customerResults, isFetching: searchingCustomers } = useQuery({
    queryKey: ['customers', 'select', trimmedKeyword],
    queryFn: () => customersApi.list({ name: trimmedKeyword, scope: 'department', page: 1, page_size: 10 }),
    enabled: open && !isEdit && customerId === 0 && trimmedKeyword.length > 0,
  })

  // 打开/切换目标时重置表单并聚焦首 个待填项
  useEffect(() => {
    if (!open) return
    setKeyword('')
    setPickedCustomer(null)
    setHighlightIndex(0)
    if (record) {
      form.reset({
        customer_id: record.customer_id,
        type_ids: record.types.map(t => t.id),
        subject: record.subject,
        content: record.content || '',
        images: record.images.map(i => i.path),
        next_service_at: (record.next_service_at || '').slice(0, 10),
        category_id: '',
      })
    } else {
      form.reset({
        customer_id: customer?.id || 0,
        type_ids: [],
        subject: '',
        content: '',
        images: [],
        next_service_at: '',
        category_id: '',
      })
    }
    const focusTimer = setTimeout(() => {
      if (isEdit || customer) {
        subjectInputRef.current?.focus()
      } else {
        searchInputRef.current?.focus()
      }
    }, 50)
    return () => clearTimeout(focusTimer)
  }, [open, record, customer, form, isEdit])

  const createMutation = useCreateServiceRecord()
  const updateMutation = useUpdateServiceRecord()
  const submitting = createMutation.isPending || updateMutation.isPending

  const selectedCustomerName = record?.customer_name || customer?.name || pickedCustomer?.name || keyword
  const selectedCategoryName = record ? undefined : (customer?.category?.name ?? pickedCustomer?.category?.name)
  const showCustomerCard = isEdit || customerId > 0
  const allowChangeCustomer = !isEdit

  // 芯片列表 = 启用类型（options 仅返回启用）+ 编辑时该记录已有的停用类型（可保留/移除，移除后不可再选）
  const stoppedTypes = useMemo(
    () => (record?.types ?? []).filter(t => !t.enabled && typeIds.includes(t.id)),
    [record, typeIds],
  )
  const chipTypes = useMemo(
    () => [...(typeOptions ?? []), ...stoppedTypes],
    [typeOptions, stoppedTypes],
  )
  const selectedTypes = useMemo(
    () => chipTypes.filter(t => typeIds.includes(t.id)),
    [chipTypes, typeIds],
  )
  const needsImage = selectedTypes.some(t => t.is_avatar) && images.length === 0

  const selectCustomer = (c: Customer) => {
    form.setValue('customer_id', c.id, { shouldValidate: true })
    setPickedCustomer(c)
    setKeyword(c.name)
    setHighlightIndex(0)
    subjectInputRef.current?.focus()
  }

  const changeCustomer = () => {
    form.setValue('customer_id', 0, { shouldValidate: false })
    setPickedCustomer(null)
    setKeyword('')
    requestAnimationFrame(() => searchInputRef.current?.focus())
  }

  const toggleType = (id: number) => {
    const next = typeIds.includes(id) ? typeIds.filter(t => t !== id) : [...typeIds, id]
    form.setValue('type_ids', next, { shouldValidate: true })
  }


  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const results = customerResults?.list ?? []
    if (results.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex(i => (i + 1) % results.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex(i => (i - 1 + results.length) % results.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const target = results[highlightIndex]
      if (target) selectCustomer(target)
    }
  }

  const submit = form.handleSubmit(async values => {
    const content = values.content.trim() ? values.content : undefined
    try {
      if (isEdit && record) {
        await updateMutation.mutateAsync({
          id: record.id,
          data: {
            type_ids: values.type_ids,
            subject: values.subject.trim(),
            content,
            images: values.images.map(url => ({ url })),
            next_service_at: values.next_service_at,
          },
        })
      } else {
        await createMutation.mutateAsync({
          customer_id: values.customer_id,
          type_ids: values.type_ids,
          subject: values.subject.trim(),
          content,
          images: values.images.map(url => ({ url })),
          next_service_at: values.next_service_at,
          category_id: values.category_id ? Number(values.category_id) : undefined,
        })
      }
      onOpenChange(false)
      onSaved?.()
    } catch (error) {
      showError(error, isEdit ? '更新服务记录失败' : '录入服务记录失败')
    }
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? '编辑服务记录' : '录入服务记录'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? `${record?.customer_name ?? ''} · ${formatDateTime(record?.record_at)} 录入`
              : '选择客户与服务类型，快捷完成一次服务登记'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex min-h-0 flex-col">
            <ScrollArea className="-mx-2 max-h-[64vh]">
              <div className="space-y-5 px-2 pb-1">
                {/* 客户：谁 */}
                <section className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <FormLabel>
                      客户 <span className="text-destructive">*</span>
                    </FormLabel>
                    {showCustomerCard && allowChangeCustomer && customerId > 0 && !customer && (
                      <Button type="button" variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={changeCustomer}>
                        <RefreshCw className="size-3.5" />
                        更换
                      </Button>
                    )}
                  </div>

                  {showCustomerCard ? (
                    <div className="flex items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2.5">
                      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary">
                        {(selectedCustomerName || '?').slice(0, 1)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{selectedCustomerName || '-'}</p>
                        {selectedCategoryName && (
                          <p className="truncate text-xs text-muted-foreground">{selectedCategoryName}</p>
                        )}
                      </div>
                      {isEdit ? (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Lock className="size-3.5" />
                          不可变更
                        </span>
                      ) : customer ? (
                        <span className="text-xs text-muted-foreground">当前客户</span>
                      ) : null}
                    </div>
                  ) : (
                    <div>
                      <div className="relative">
                        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          ref={searchInputRef}
                          value={keyword}
                          className="pl-9"
                          placeholder="输入客户名搜索，↑↓ 选择，回车确认"
                          onChange={e => {
                            setKeyword(e.target.value)
                            setHighlightIndex(0)
                            if (customerId !== 0) {
                              form.setValue('customer_id', 0, { shouldValidate: false })
                            }
                          }}
                          onKeyDown={handleSearchKeyDown}
                        />
                      </div>
                      {customerId === 0 && trimmedKeyword.length > 0 && (
                        <div className="mt-1.5 overflow-hidden rounded-lg border">
                          {searchingCustomers ? (
                            <div className="space-y-2 p-2">
                              {[0, 1, 2].map(i => (
                                <Skeleton key={i} className="h-9 w-full" />
                              ))}
                            </div>
                          ) : (customerResults?.list ?? []).length > 0 ? (
                            <div className="divide-y">
                              {(customerResults?.list ?? []).map((c, index) => (
                                <button
                                  key={c.id}
                                  type="button"
                                  className={cn(
                                    'flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors',
                                    highlightIndex === index ? 'bg-accent' : 'hover:bg-accent/60',
                                  )}
                                  onMouseEnter={() => setHighlightIndex(index)}
                                  onClick={() => selectCustomer(c)}
                                >
                                  <span className="truncate font-medium">{c.name}</span>
                                  {c.category?.name && (
                                    <span className="ml-2 shrink-0 text-xs text-muted-foreground">{c.category.name}</span>
                                  )}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <p className="px-3 py-2.5 text-sm text-muted-foreground">未找到匹配的客户</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {form.formState.errors.customer_id && (
                    <p className="text-sm font-medium text-destructive">{form.formState.errors.customer_id.message}</p>
                  )}
                </section>

                {/* 类型：什么服务 */}
                <section className="space-y-1.5">
                  <FormLabel>
                    服务类型 <span className="text-destructive">*</span>
                    <span className="ml-1 font-normal text-muted-foreground">可多选</span>
                  </FormLabel>
                  <TooltipProvider delayDuration={200}>
                    <div className="flex flex-wrap gap-2">
                      {typesLoading
                        ? [0, 1, 2, 3].map(i => <Skeleton key={i} className="h-9 w-24 rounded-full" />)
                        : chipTypes.map(t => (
                            <TypeChip
                              key={t.id}
                              type={t}
                              selected={typeIds.includes(t.id)}
                              stopped={!t.enabled}
                              onToggle={() => toggleType(t.id)}
                            />
                          ))}
                      {!typesLoading && chipTypes.length === 0 && (
                        <p className="text-sm text-muted-foreground">暂无服务类型，请先在「设置 → 服务类型」中添加</p>
                      )}
                    </div>
                  </TooltipProvider>
                  {form.formState.errors.type_ids && (
                    <p className="text-sm font-medium text-destructive">{form.formState.errors.type_ids.message}</p>
                  )}
                </section>

                {/* 叙述：主题 / 内容 / 图片 */}
                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        服务主题 <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          ref={e => {
                            field.ref(e)
                            subjectInputRef.current = e
                          }}
                          maxLength={255}
                          placeholder="如：售后回访、活动跟进"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="content"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        服务内容
                        <span className="ml-1 font-normal text-muted-foreground">选填，≤5000 字</span>
                      </FormLabel>
                      <FormControl>
                        <Textarea rows={3} {...field} placeholder="记录本次服务的沟通要点与结论" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <section className="space-y-1.5">
                  <FormLabel>
                    服务图片
                    {selectedTypes.some(t => t.is_avatar) && (
                      <span className="ml-1 font-normal text-muted-foreground">所选类型含需图项</span>
                    )}
                  </FormLabel>
                  <MultiImageUploadField value={images} onChange={urls => form.setValue('images', urls, { shouldValidate: true })} />
                  {needsImage && (
                    <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
                      <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                      <p>已选类型要求上传图片，建议补充现场截图后再提交</p>
                    </div>
                  )}
                </section>

                <Separator />

                {/* 跟进：下次回访 / 顺带改分类 */}
                <FormField
                  control={form.control}
                  name="next_service_at"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <span className="inline-flex items-center gap-1">
                          <CalendarDays className="size-4 text-muted-foreground" />
                          下次回访时间 <span className="text-destructive">*</span>
                        </span>
                      </FormLabel>
                      <div className="flex flex-wrap items-center gap-2">
                        <FormControl>
                          <Input type="date" className="w-40" {...field} />
                        </FormControl>
                        {QUICK_DATE_OPTIONS.map(option => {
                          const value = format(addDays(new Date(), option.days), 'yyyy-MM-dd')
                          const active = field.value === value
                          return (
                            <button
                              key={option.days}
                              type="button"
                              aria-pressed={active}
                              onClick={() => form.setValue('next_service_at', value, { shouldValidate: true })}
                              className={cn(
                                'rounded-full border px-2.5 py-1 text-xs transition-colors',
                                active
                                  ? 'border-primary/50 bg-primary/10 text-primary'
                                  : 'border-input text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                              )}
                            >
                              {option.label}
                            </button>
                          )
                        })}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!isEdit && (
                  <FormField
                    control={form.control}
                    name="category_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          顺带修改客户类型
                          <span className="ml-1 font-normal text-muted-foreground">选填</span>
                        </FormLabel>
                        <Select value={field.value || 'none'} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="w-52">
                              <SelectValue placeholder="不修改" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="none">不修改</SelectItem>
                            {(categories ?? []).map(c => (
                              <SelectItem key={c.id} value={String(c.id)}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </ScrollArea>

            <DialogFooter className="mt-5">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="mr-2 size-4 animate-spin" />}
                {isEdit ? '保存' : '录入'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
